import { Box, Button, Divider, Loader, Text } from '@wix/design-system';
import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { AppContextProvider } from '../AppLoader';
import ConfirmEmailIllustration from '../../assets/images/confirmEmail.svg';
import { DialogFormContainer } from '../DialogContainer';
import { dataHooks } from '../../dataHooks';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { Trans, useTranslation } from 'react-i18next';
import { ThemedText, ThemedTextButton } from '../ThemedComponents';
import { InputFieldWithLabel } from '../InputField';
import { VerificationState } from '../PostLogin/VerifyButton';
import s from '../PostLogin/PostLogin.scss';
import { PostSignupEmailConfirmStore } from '../../stores/postSignupEmailConfirm';
import EmailClientButton from './EmailClientButton';
import {
  postSignupEmailConfirmationClicksSrc5Evid1939,
  postSignupEmailConfirmationMainScreenDisplaySrc5Evid1943,
} from '@wix/bi-logger-identity-data/v2';
import { BI_ORIGIN, BI_BUTTON_NAMES } from '../../stores/constants';

const PostSignupEmailConfirm: React.FC = observer(() => {
  const {
    rootStore: { postSignupEmailConfirmStore },
  } = useContext(AppContextProvider);

  const handleConfirmEmail = async () => {
    if (
      postSignupEmailConfirmStore.verificationState !==
      VerificationState.Verifying
    ) {
      postSignupEmailConfirmStore.sendBiEvent(
        postSignupEmailConfirmationClicksSrc5Evid1939({
          cta_name: BI_BUTTON_NAMES.CONFIRM_EMAIL,
          origin: BI_ORIGIN.POST_SIGNUP,
        }),
      );
      await postSignupEmailConfirmStore.verifyCode();
    }
  };

  const handleEmailClientClick = (provider: string) => {
    postSignupEmailConfirmStore.sendBiEvent(
      postSignupEmailConfirmationClicksSrc5Evid1939({
        cta_name: provider,
        origin: BI_ORIGIN.POST_SIGNUP,
      }),
    );
  };

  const handleResendClick = () => {
    postSignupEmailConfirmStore.sendBiEvent(
      postSignupEmailConfirmationClicksSrc5Evid1939({
        cta_name: BI_BUTTON_NAMES.RESEND_EMAIL,
        origin: BI_ORIGIN.POST_SIGNUP,
      }),
    );
    postSignupEmailConfirmStore.resendCode();
  };

  useEffect(() => {
    postSignupEmailConfirmStore.sendBiEvent(
      postSignupEmailConfirmationMainScreenDisplaySrc5Evid1943({
        origin: BI_ORIGIN.POST_SIGNUP,
      }),
    );
  }, []);

  return (
    <Box direction="vertical" width="100%" align="center">
      <DialogFormContainer
        dataHook={dataHooks.enterCode.container}
        mainHeaderProps={{ showBackButton: false, disableLogoClick: true }}
      >
        <EmailConfirmationHeader email={postSignupEmailConfirmStore.email} />

        <Box direction="vertical" align="center" gap="16px" width="320">
          <EmailConfirmationForm
            store={postSignupEmailConfirmStore}
            onConfirmEmail={handleConfirmEmail}
            onResendClick={handleResendClick}
          />
          <EmailClientLinks
            email={postSignupEmailConfirmStore.email}
            onEmailClientClick={handleEmailClientClick}
          />
          <EmailConfirmationFooter />
        </Box>
      </DialogFormContainer>
    </Box>
  );
});

const EmailConfirmationHeader: React.FC<{
  email: string;
}> = ({ email }) => {
  const { t } = useTranslation();
  return (
    <DialogHeader>
      <Box>
        <ConfirmEmailIllustration />
      </Box>
      <Box height="10px"></Box>
      <DialogHeader.Title as="h1" flow="secondaryFlow">
        {t('emailConfirmation.signUp.title')}
      </DialogHeader.Title>
      <Box height="2px"></Box>
      <DialogHeader.SubTitleSmall>
        <Trans
          i18nKey="emailConfirmation.signUp.description"
          values={{ email }}
          components={[<ThemedText weight="bold" />]}
        />
      </DialogHeader.SubTitleSmall>
    </DialogHeader>
  );
};

const EmailConfirmationForm: React.FC<{
  store: PostSignupEmailConfirmStore;
  onConfirmEmail: () => void;
  onResendClick: () => void;
}> = observer(({ store, onConfirmEmail, onResendClick }) => {
  const { t } = useTranslation();

  return (
    <>
      <Box width="100%" direction="vertical">
        <InputFieldWithLabel
          formField={store.confirmCode}
          required
          autoFocus
          label={t('enter_code.code_input_placeholder')}
          value={store.confirmCode.value}
          onEnterPressed={onConfirmEmail}
          dataHook={dataHooks.enterCode.codeInput}
        />
      </Box>
      <VerifyButton store={store} onConfirmEmail={onConfirmEmail} />
      <ResendSection store={store} onResendClick={onResendClick} />
      <Box height="60px" /> {/* Spacer */}
    </>
  );
});

const VerifyButton: React.FC<{
  store: PostSignupEmailConfirmStore;
  onConfirmEmail: () => void;
}> = observer(({ store, onConfirmEmail }) => {
  const { t } = useTranslation();
  return (
    <Button
      dataHook={dataHooks.enterCode.verifyButton}
      fullWidth
      size="large"
      type="submit"
      skin="standard"
      priority="primary"
      onClick={onConfirmEmail}
    >
      {store.verificationState === VerificationState.Verifying ? (
        <Loader size="tiny" status="loading" color="white" />
      ) : (
        t('emailConfirmation.signUp.primaryCta.confirmEmail')
      )}
    </Button>
  );
});

const ResendSection: React.FC<{
  store: PostSignupEmailConfirmStore;
  onResendClick: () => void;
}> = observer(({ store, onResendClick }) => {
  const { t } = useTranslation();
  return (
    <Box paddingTop={1} direction="vertical" align="center" width="100%">
      {!store.resendSuccessfullyIndication && (
        <ThemedTextButton
          size="small"
          as="a"
          onClick={onResendClick}
          underline="none"
          skin="standard"
        >
          {t('emailConfirmation.signUp.secondaryCta.resendCode')}
        </ThemedTextButton>
      )}

      {store.resendSuccessfullyIndication && (
        <Text size="small" skin="disabled">
          {t('emailConfirmation.signUp.code.resend.indication', {
            time: store.resendThrottleTimerValue.toString(),
          })}
        </Text>
      )}

      {store.resendErrorMessage && (
        <Box marginInlineStart="6px" maxWidth="100%">
          <ThemedText
            className={s.resendErrorMessage}
            skin="error"
            size="small"
          >
            {store.resendErrorMessage}
          </ThemedText>
        </Box>
      )}
    </Box>
  );
});

const EmailClientLinks: React.FC<{
  email: string;
  onEmailClientClick: (provider: string) => void;
}> = ({ email, onEmailClientClick }) => {
  return (
    <Box direction="horizontal" align="center" gap="30px">
      <EmailClientButton
        provider="gmail"
        email={email}
        onClientClick={onEmailClientClick}
      />
      <Box height="16px">
        <Divider direction="vertical" />
      </Box>
      <EmailClientButton
        provider="outlook"
        email={email}
        onClientClick={onEmailClientClick}
      />
    </Box>
  );
};

const EmailConfirmationFooter: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Text size="tiny" skin="standard" secondary>
      {t('emailConfirmation.signUp.footerText.checkSpam')}
    </Text>
  );
};

export default PostSignupEmailConfirm;
